import { useSelector } from 'react-redux'

import type { CollectionUUID, Recipe, RecipeCoreId } from '@library/menu-service'

import { useBasketRecipes } from 'hooks/basket/useBasketRecipes'
import { getSelectedRecipeVariants } from 'routes/Menu/selectors/variants'

import type { EnrichedRecipeSet } from './useCollectionEnrichedRecipeSets'
import { useCollectionEnrichedRecipeSets } from './useCollectionEnrichedRecipeSets'

type RecipeSetWithSelectedRecipe = {
  recipeSet: EnrichedRecipeSet
  selectedRecipe: Recipe
}

/**
 * @description If recipe is not in stock, select first option that is in stock
 */
const selectAvailableOption =
  (isRecipeInBasket: (id: RecipeCoreId) => boolean) =>
  (recipeSet: EnrichedRecipeSet): RecipeSetWithSelectedRecipe => {
    // if recipe is in stock - no further processing
    if (recipeSet.baseRecipe.isAvailable || isRecipeInBasket(recipeSet.baseRecipe.coreId)) {
      return {
        recipeSet,
        selectedRecipe: recipeSet.baseRecipe,
      }
    }

    const availableOptions = recipeSet.options.filter(
      (option) => option.isAvailable || isRecipeInBasket(option.coreId),
    )
    // no alternatives in stock - nothing to replace with
    if (availableOptions.length === 0) {
      return {
        recipeSet,
        selectedRecipe: recipeSet.baseRecipe,
      }
    }

    // found an alternative - replace original recipe with it
    return {
      recipeSet,
      selectedRecipe: availableOptions[0],
    }
  }

/**
 * @description Put out of stock recipes last, keeping the order of the rest
 */
const byRecipeAvailability =
  (isRecipeInBasket: (id: RecipeCoreId) => boolean) =>
  (a: RecipeSetWithSelectedRecipe, b: RecipeSetWithSelectedRecipe) => {
    const aIsAvailable = a.selectedRecipe.isAvailable || isRecipeInBasket(a.selectedRecipe.coreId)
    const bIsAvailable = b.selectedRecipe.isAvailable || isRecipeInBasket(b.selectedRecipe.coreId)

    if (aIsAvailable === bIsAvailable) {
      return 0
    }

    if (aIsAvailable && !bIsAvailable) {
      return -1
    } else {
      return 1
    }
  }

const overwriteUserSelectedRecipeOption =
  (selectedRecipeVariants: Record<string, RecipeCoreId>) => (item: RecipeSetWithSelectedRecipe) => {
    if (!selectedRecipeVariants || !selectedRecipeVariants[item.recipeSet.id]) {
      return item
    }

    const selectedOption = selectedRecipeVariants[item.recipeSet.id] as RecipeCoreId
    if (item.recipeSet.baseRecipe.coreId === selectedOption) {
      return {
        recipeSet: item.recipeSet,
        selectedRecipe: item.recipeSet.baseRecipe,
      }
    }

    const matchingCollectionRecipeOption = item.recipeSet.options.find(
      (option) => option.coreId === selectedOption,
    )
    if (matchingCollectionRecipeOption) {
      return {
        recipeSet: item.recipeSet,
        selectedRecipe: matchingCollectionRecipeOption,
      }
    }

    return item
  }

export const useCollectionRecipes = (collectionId: CollectionUUID) => {
  const selectedVariants: Record<CollectionUUID, Record<string, RecipeCoreId>> = useSelector(
    getSelectedRecipeVariants,
  )
  const selectedRecipeVariants = selectedVariants[collectionId] || {}

  const { basketHasRecipe } = useBasketRecipes()

  const collectionRecipeSets = useCollectionEnrichedRecipeSets(collectionId)

  const selectedRecipes = collectionRecipeSets
    .map(selectAvailableOption(basketHasRecipe)) // replace base recipe if it is not in stock
    .sort(byRecipeAvailability(basketHasRecipe)) // put out of stock recipes last
    .map(overwriteUserSelectedRecipeOption(selectedRecipeVariants)) // if user has explicitly selected an option, use that

  return selectedRecipes.map(({ recipeSet, selectedRecipe }) => ({
    recipe: selectedRecipe,
    originalId: recipeSet.baseRecipe.coreId,
    reference: recipeSet.id,
  }))
}
